import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, IconButton, Menu, MenuItem, SearchField, Typography } from '@carto/meridian-ds/components'
import { ListItemText, Theme, styled, useMediaQuery, ListItemIcon, Tooltip } from '@mui/material'
import { ExpandLess, ExpandMore, LocalOfferOutlined } from '@mui/icons-material'
import useTags from '../hooks/useTags'
import TagColor from './TagColor'

interface TagFilterSelector {
  value?: string[]
  onFilterChange: (tags: string[]) => void
}

const TabletIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'focused'
})<{ focused?: boolean }>(({ theme, focused }) => ({
  '& button': {
    background: focused ? theme.palette.action.hover : 'unset'
  }
}))
const DesktopButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'focused'
})<{ focused?: boolean }>(({ theme, focused }) => ({
  '&': {
    background: focused ? theme.palette.action.hover : 'unset'
  }
}))

function TagFilterSelector({ value = [], onFilterChange }: TagFilterSelector) {
  const { data: tags, isFetched } = useTags()
  const searchRef = useRef<HTMLInputElement>()
  const [search, setSearch] = useState('')
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'))

  const existsValueInTagsList = useCallback(
    (value) => {
      return tags?.find((t) => t === value)
    },
    [tags]
  )
  const filteredTags = useMemo(() => {
    return tags?.filter((t) => t.toLowerCase().includes(search.toLowerCase())) || []
  }, [tags, search])
  const showEmpty = !filteredTags.length
  const selectedTags = useMemo(() => {
    return value.filter(existsValueInTagsList)
  }, [value, existsValueInTagsList])

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const search = event.target.value
      setSearch(search)
    },
    [setSearch]
  )

  const handleClick = (tag) => {
    let selectedTags: string[] = []
    if (value.some?.((t) => t === tag)) {
      selectedTags = value.filter((t) => t !== tag)
    } else {
      selectedTags = [...value, tag]
    }
    onFilterChange(selectedTags)
  }

  const isSelected = (tag: string) => {
    return value?.some?.((t) => t === tag)
  }

  const openDropdown = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const closeDropdown = () => {
    setAnchorEl(null)
  }

  useEffect(() => {
    if (value && isFetched) {
      const filteredValidTags = value.filter(existsValueInTagsList)
      // Only call onFilterChange if the filtered tags are different from the current value
      if (filteredValidTags.length !== value.length || !filteredValidTags.every((tag) => value.includes(tag))) {
        onFilterChange(filteredValidTags)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isFetched, existsValueInTagsList])

  return (
    <>
      {isTablet ? (
        <TabletIconButton onClick={openDropdown} focused={open} icon={<LocalOfferOutlined />} />
      ) : (
        <DesktopButton
          endIcon={open ? <ExpandLess /> : <ExpandMore />}
          color="default"
          onClick={openDropdown}
          focused={open}
        >
          {selectedTags?.length ? `${selectedTags.length} selected` : 'Tags'}
        </DesktopButton>
      )}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeDropdown}
        autoFocus={false}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: (theme) => `${theme.spacing(20)} !important`,
              maxWidth: (theme) => theme.spacing(40)
            }
          }
        }}
      >
        <MenuItem fixed disableGutters>
          <SearchField
            inputRef={searchRef}
            variant="transparent"
            onChange={handleChange}
            placeholder="Search tags"
            onKeyDown={(e) => e.stopPropagation()}
            autoFocus
          />
        </MenuItem>
        {filteredTags.map((tag: string) => (
          <MenuItem key={tag} onClick={() => handleClick(tag)} selected={isSelected(tag)}>
            <ListItemIcon sx={{ justifyContent: 'center' }}>
              <TagColor name={tag} />
            </ListItemIcon>
            <ListItemText>
              <Tooltip title={tag}>
                <Typography overflow="hidden" textOverflow="ellipsis" variant="body2">
                  {tag}
                </Typography>
              </Tooltip>
            </ListItemText>
          </MenuItem>
        ))}
        {showEmpty && (
          <MenuItem sx={{ pointerEvents: 'none' }}>
            <Typography variant="body2" color="textSecondary">
              {!search ? 'No tags' : 'No tags matching your search'}
            </Typography>
          </MenuItem>
        )}
      </Menu>
    </>
  )
}

export default TagFilterSelector
